import React, { useState, useEffect } from 'react';
import { StudyModule, StudySlide, StudySlideType, CertificationType } from '../../types';
import { StorageService } from '../../services/storageService';
import { useAuth } from '../../components/AuthBridge';
import { getSafeEmbedUrl, getEmbedSandbox, EMBED_ALLOW_POLICY, inspectEmbedUrl } from '../../services/embedService';
import { EmbeddedPhasePlayer } from '../../components/EmbeddedPhasePlayer';
import { 
    Plus, Trash2, Save, X, BookOpen, Layers, 
    CheckCircle, Info, Sparkles, Loader2, Wand2, 
    ArrowUp, ArrowDown, ChevronUp, ChevronDown, Brain, 
    Image as ImageIcon, Layout, Target, Zap, 
    AlertTriangle, MessageSquare, ClipboardList, ShieldAlert,
    Star, Lightbulb, Ban, ChevronRight, ListTodo, RefreshCw, Link as LinkIcon,
    Settings, Maximize2, FileText, Activity, Search, Video
} from 'lucide-react';
import { 
    generateModuleArchitecture, 
    generateStudySlide, 
    generateInfographicLogic,
    generateTutorVisual,
    refineVisualPrompt,
    generateSlideObservations,
    generateStudyStrategy
} from '../../services/geminiService';

const SLIDE_TYPES: { type: StudySlideType; label: string; icon: any; color: string }[] = [
    { type: 'infographic', label: 'Visual Guide', icon: ImageIcon, color: 'bg-indigo-500' },
    { type: 'overview', label: 'Big Picture', icon: Layout, color: 'bg-medical-500' },
    { type: 'definitions', label: 'Glossary', icon: BookOpen, color: 'bg-emerald-500' },
    { type: 'responsibilities', label: 'Scope & Do/Dont', icon: Ban, color: 'bg-rose-500' },
    { type: 'process', label: 'Protocol Steps', icon: ListTodo, color: 'bg-amber-500' },
    { type: 'examFocus', label: 'Board Traps', icon: ShieldAlert, color: 'bg-orange-600' },
    { type: 'scenario', label: 'Case Study', icon: Zap, color: 'bg-indigo-600' },
    { type: 'review', label: 'Synthesis', icon: Star, color: 'bg-purple-600' },
    { type: 'embedded', label: 'Embedded Phase', icon: LinkIcon, color: 'bg-indigo-700' }
];

export const StudyContentManager: React.FC = () => {
    const [certId, setCertId] = useState('');
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [modules, setModules] = useState<StudyModule[]>([]);
    const [selectedModule, setSelectedModule] = useState<StudyModule | null>(null);
    const [activeSlideIndex, setActiveSlideIndex] = useState(-1);
    
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isHydrating, setIsHydrating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // AI Strategy / Suggestions
    const [isBlueprinting, setIsBlueprinting] = useState(false);
    const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
    
    // AI Modal States
    const [showFastBuild, setShowFastBuild] = useState(false);
    const [fastBuildTopic, setFastBuildTopic] = useState('');
    const [isBuilding, setIsBuilding] = useState(false);
    
    // Slide AI States
    const [isGeneratingSlide, setIsGeneratingSlide] = useState(false);
    const [isBatchSynthesizing, setIsBatchSynthesizing] = useState(false);
    const [batchProgress, setBatchProgress] = useState(0);
    const [isGeneratingVisual, setIsGeneratingVisual] = useState(false);
    const [isGeneratingLogic, setIsGeneratingLogic] = useState(false);
    const [isRefiningPrompt, setIsRefiningPrompt] = useState(false);
    
    const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);

    useEffect(() => {
        StorageService.getCertifications().then(data => {
            setCerts(data);
            if (data.length > 0) setCertId(data[0].id);
        });
    }, []);

    useEffect(() => {
        if (!certId) return;
        const load = async () => {
            setIsLoading(true);
            try {
                // LAZY LOAD: Metadata summary only for the sidebar
                const data = await StorageService.getStudyModulesSummary(certId);
                setModules(data || []);
                setSelectedModule(null);
                setActiveSlideIndex(-1);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [certId]);

    const handleSelectModule = async (m: StudyModule) => {
        setStatus(null);
        setActiveSlideIndex(-1);
        
        // Use existing full data if already hydrated in session
        if (m.slides && m.slides.length > 0) {
            setSelectedModule(m);
            return;
        }

        // Hydrate from registry
        setIsHydrating(true);
        setSelectedModule(m); 
        try {
            const full = await StorageService.getStudyModuleById(certId, m.id);
            setSelectedModule(full);
            // Cache full data in main list
            setModules(prev => prev.map(item => item.id === m.id ? full : item));
        } catch (e) {
            setStatus({ type: 'error', text: 'Theoretical logic hydration failed.' });
        } finally {
            setIsHydrating(false);
        }
    };

    const activeCert = certs.find(c => c.id === certId);

    const handleSave = async () => {
        if (!certId || !selectedModule) return;
        setIsSaving(true);
        setStatus(null);
        try {
            // TARGETED SAVE: Only commit the active theoretical node
            await StorageService.saveStudyModule(certId, selectedModule);
            setStatus({ type: 'success', text: `Registry synchronized: ${selectedModule.title}` });
        } catch (e) {
            setStatus({ type: 'error', text: 'Logic commit failure.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSuggestStrategy = async () => {
        setIsBlueprinting(true);
        setStatus(null);
        try {
            const topics = await generateStudyStrategy(activeCert?.name || 'Medical');
            setSuggestedTopics(topics);
            setStatus({ type: 'success', text: 'AI Strategy Synthesized. Expand topics to Provision.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Strategy engine interrupted.' });
        } finally {
            setIsBlueprinting(false);
        }
    };

    const handleProvisionFromTopic = async (topic: string, autoBuild: boolean = false) => {
        if (autoBuild) {
            setFastBuildTopic(topic);
            setShowFastBuild(true);
            setStatus(null);
        } else {
            const newModule: StudyModule = {
                id: `module-${Date.now()}`,
                title: topic,
                icon: 'BookOpen',
                slides: []
            };
            setModules(prev => [...prev, newModule]);
            setSelectedModule(newModule);
            setStatus({ type: 'success', text: `Empty unit provisioned: ${topic}` });
        }
        setSuggestedTopics(prev => prev.filter(t => t !== topic));
    };

    const performFastBuild = async (topic: string) => {
        if (!topic.trim() || !certId) return;
        setIsBuilding(true);
        setStatus(null);
        try {
            const certName = activeCert?.name || 'Medical';
            const arch = await generateModuleArchitecture(topic, certName);
            
            const newModule: StudyModule = {
                id: `module-${Date.now()}`,
                title: topic,
                icon: arch.slides[0]?.type === 'infographic' ? 'Sparkles' : 'BookOpen',
                slides: arch.slides.map((s: any, idx: number) => ({
                    id: `slide-${Date.now()}-${idx}`,
                    type: s.type as StudySlideType,
                    title: s.title,
                    data: getDefaultDataForType(s.type as StudySlideType)
                }))
            };

            setModules(prev => [...prev, newModule]);
            setSelectedModule(newModule);
            setStatus({ type: 'success', text: 'Module architecture architected. You can now Batch Synthesize the content.' });
            return newModule;
        } catch (e: any) {
            console.error("AI Fast Build Failure:", e);
            setStatus({ 
                type: 'error', 
                text: `AI Builder failed: ${e.message || 'Check API connection and topic details.'}` 
            });
        } finally {
            setIsBuilding(false);
        }
    };

    const handleFastBuild = async () => {
        const result = await performFastBuild(fastBuildTopic);
        if (result) {
            setShowFastBuild(false);
            setFastBuildTopic('');
        }
    };

    const handleBatchSynthesize = async () => {
        if (!selectedModule) return;
        if (!confirm('This will overwrite any existing content in this module with AI-synthesized clinical data. Proceed?')) return;

        setIsBatchSynthesizing(true);
        setBatchProgress(0);
        setStatus(null);

        const newSlides = [...(selectedModule.slides || [])];
        try {
            for (let i = 0; i < newSlides.length; i++) {
                const slide = newSlides[i];
                const content = await generateStudySlide(selectedModule.title, slide.type, activeCert?.name || 'Nursing');
                newSlides[i] = { ...slide, data: content };
                setBatchProgress(Math.round(((i + 1) / newSlides.length) * 100));
            }
            const updated = { ...selectedModule, slides: newSlides };
            updateModuleInList(updated);
            setStatus({ type: 'success', text: `Successfully synthesized all ${newSlides.length} slides.` });
        } catch (e: any) {
            console.error("Batch Synthesis Error:", e);
            setStatus({ type: 'error', text: `Batch synthesis interrupted: ${e.message}` });
        } finally {
            setIsBatchSynthesizing(false);
            setBatchProgress(0);
        }
    };

    const handleGenerateSlideContent = async () => {
        if (!selectedModule || activeSlideIndex < 0) return;
        setIsGeneratingSlide(true);
        try {
            const slide = selectedModule.slides[activeSlideIndex];
            const content = await generateStudySlide(selectedModule.title, slide.type, activeCert?.name || 'Nursing');
            const newSlides = [...selectedModule.slides];
            newSlides[activeSlideIndex] = { ...slide, data: content };
            updateModuleInList({ ...selectedModule, slides: newSlides });
            setStatus({ type: 'success', text: 'Educational content synthesized.' });
        } catch (e: any) {
            console.error("Slide Content Synthesis Error:", e);
            setStatus({ type: 'error', text: `Synthesis failed: ${e.message}` });
        } finally {
            setIsGeneratingSlide(false);
        }
    };

    const handleSynthesizeLogic = async () => {
        if (!selectedModule || activeSlideIndex < 0) return;
        setIsGeneratingLogic(true);
        setStatus(null);
        try {
            const slide = selectedModule.slides[activeSlideIndex];
            
            if (slide.type === 'infographic') {
                const logic = await generateInfographicLogic(selectedModule.title, slide.title);
                const newSlides = [...selectedModule.slides];
                newSlides[activeSlideIndex].data = {
                    ...newSlides[activeSlideIndex].data,
                    visualAlt: logic.visualAlt,
                    observations: logic.observations
                };
                updateModuleInList({ ...selectedModule, slides: newSlides });
                setStatus({ type: 'success', text: 'Visual logic and focus points synthesized.' });
            } else if (slide.type === 'overview') {
                const observations = await generateSlideObservations(selectedModule.title, slide.title || 'Overview', 'overview');
                const newSlides = [...selectedModule.slides];
                newSlides[activeSlideIndex].data = {
                    ...newSlides[activeSlideIndex].data,
                    observations
                };
                updateModuleInList({ ...selectedModule, slides: newSlides });
                setStatus({ type: 'success', text: 'Overview focus points synthesized.' });
            }
        } catch (e: any) {
            console.error("Logic Synthesis Error:", e);
            setStatus({ type: 'error', text: `Logic synthesis failed: ${e.message}` });
        } finally {
            setIsGeneratingLogic(false);
        }
    };

    const handleRefinePrompt = async () => {
        if (!selectedModule || activeSlideIndex < 0) return;
        const slide = selectedModule.slides[activeSlideIndex];
        setIsRefiningPrompt(true);
        try {
            const contentString = JSON.stringify(slide.data);
            const refined = await refineVisualPrompt(selectedModule.title, slide.title || slide.type, contentString);
            updateSlideField('visualAlt', refined);
            setStatus({ type: 'success', text: 'Visual prompt refined for high-fidelity generation.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Prompt refinement failed.' });
        } finally {
            setIsRefiningPrompt(false);
        }
    };

    const handleSynthesizeVisual = async () => {
        if (!selectedModule || activeSlideIndex < 0) return;
        const slide = selectedModule.slides[activeSlideIndex];
        if (!slide.data.visualAlt) {
            setStatus({ type: 'error', text: 'Synthesize logic or enter a prompt first.' });
            return;
        }
        setIsGeneratingVisual(true);
        setStatus(null);
        try {
            const visual = await generateTutorVisual(selectedModule.title, slide.data.visualAlt);
            if (visual) {
                const newSlides = [...selectedModule.slides];
                newSlides[activeSlideIndex].data = {
                    ...newSlides[activeSlideIndex].data,
                    imageUrl: visual
                };
                updateModuleInList({ ...selectedModule, slides: newSlides });
                setStatus({ type: 'success', text: 'Clinical illustration generated.' });
            } else {
                throw new Error("Empty visual returned");
            }
        } catch (e: any) {
            console.error("Visual Generation Error:", e);
            setStatus({ type: 'error', text: `Visual generation failed: ${e.message}` });
        } finally {
            setIsGeneratingVisual(false);
        }
    };

    const updateModuleInList = (updated: StudyModule) => {
        setSelectedModule(updated);
        setModules(modules.map(m => m.id === updated.id ? updated : m));
    };

    const addManualSlide = (type: StudySlideType) => {
        if (!selectedModule) return;
        const newSlide: StudySlide = {
            id: `slide-${Date.now()}`,
            type,
            title: SLIDE_TYPES.find(t => t.type === type)?.label,
            data: getDefaultDataForType(type)
        };
        const updated = { ...selectedModule, slides: [...(selectedModule.slides || []), newSlide] };
        updateModuleInList(updated);
        setActiveSlideIndex(updated.slides.length - 1);
    };

    const moveSlide = (direction: 'up' | 'down') => {
        if (!selectedModule || activeSlideIndex < 0) return;
        const newSlides = [...selectedModule.slides];
        const target = direction === 'up' ? activeSlideIndex - 1 : activeSlideIndex + 1;
        if (target < 0 || target >= newSlides.length) return;
        [newSlides[activeSlideIndex], newSlides[target]] = [newSlides[target], newSlides[activeSlideIndex]];
        updateModuleInList({ ...selectedModule, slides: newSlides });
        setActiveSlideIndex(target);
    };

    const deleteSlide = () => {
        if (!selectedModule || activeSlideIndex < 0) return;
        if (!confirm('Discard this educational step?')) return;
        const newSlides = selectedModule.slides.filter((_, i) => i !== activeSlideIndex);
        updateModuleInList({ ...selectedModule, slides: newSlides });
        setActiveSlideIndex(-1);
    };

    const updateSlideField = (field: string, value: any) => {
        if (!selectedModule || activeSlideIndex < 0) return;
        const newSlides = [...selectedModule.slides];
        if (field === 'title') {
            newSlides[activeSlideIndex].title = value;
        } else {
            newSlides[activeSlideIndex].data = { ...newSlides[activeSlideIndex].data, [field]: value };
        }
        updateModuleInList({ ...selectedModule, slides: newSlides });
    };

    const filteredModules = modules.filter(m => 
        m.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8 animate-fade-in pb-24 h-full flex flex-col text-slate-900">
            {/* Fast Build Modal */}
            {showFastBuild && (
                <div className="fixed inset-0 z-[250] bg-slate-950/80 backdrop-blur-md flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-pop">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                <Sparkles size={24} className="text-indigo-500" />
                                AI Content Builder
                            </h3>
                            <button onClick={() => setShowFastBuild(false)} className="p-2 text-slate-400 hover:text-rose-500 transition-all"><X size={28} /></button>
                        </div>
                        <div className="p-10 space-y-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2">
                                    <Target size={14} className="text-indigo-600" /> Topic Identifier
                                </label>
                                <input 
                                    type="text" 
                                    value={fastBuildTopic} 
                                    onChange={e => setFastBuildTopic(e.target.value)}
                                    placeholder="e.g., Perineal Care Best Practices"
                                    className="w-full p-6 bg-slate-50 border border-slate-100 rounded-[2rem] outline-none font-black text-xl tracking-tighter focus:bg-white transition-all shadow-inner"
                                    autoFocus
                                />
                                <p className="text-[9px] text-slate-400 font-bold px-1">"The AI will architect a standard 8-phase teaching progression."</p>
                            </div>

                            <button 
                                onClick={handleFastBuild} 
                                disabled={isBuilding || !fastBuildTopic.trim()}
                                className="w-full py-6 bg-slate-900 text-white rounded-[1.5rem] font-black text-xs tracking-widest shadow-2xl flex items-center justify-center gap-4 disabled:opacity-50 active:scale-95 transition-all border-b-4 border-black"
                            >
                                {isBuilding ? <Loader2 className="animate-spin" size={20}/> : <Wand2 size={20} />} 
                                {isBuilding ? 'Architecting...' : 'Build Full Module Logic'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Progress Overlay for Batch Synthesis */}
            {isBatchSynthesizing && (
                <div className="fixed inset-0 z-[300] bg-slate-950/60 backdrop-blur-sm flex flex-col items-start justify-center p-12 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white p-12 rounded-[3rem] shadow-2xl w-full max-w-xl text-center space-y-8 animate-pop">
                        <div className="relative w-24 h-24 mx-auto">
                            <div className="absolute inset-0 rounded-full border-4 border-slate-100"></div>
                            <div 
                                className="absolute inset-0 rounded-full border-4 border-medical-500 border-t-transparent animate-spin"
                                style={{ animationDuration: '3s' }}
                            ></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Sparkles className="text-medical-500 animate-pulse" size={32} />
                            </div>
                        </div>
                        <div>
                            <h4 className="text-2xl font-black text-slate-900 tracking-tight">Synthesizing Curriculum</h4>
                            <p className="text-sm text-slate-400 font-medium mt-1">Generating board-accurate clinical data...</p>
                        </div>
                        <div className="w-full space-y-2">
                            <div className="flex justify-between items-end px-1">
                                <span className="text-[10px] font-black text-slate-400 tracking-widest">Global Progress</span>
                                <span className="text-xs font-black text-medical-600">{batchProgress}%</span>
                            </div>
                            <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden shadow-inner">
                                <div className="h-full bg-medical-500 transition-all duration-500 ease-out" style={{ width: `${batchProgress}%` }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6">
                <div className="flex-1 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border border-white/5">
                    <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000"><BookOpen size={160}/></div>
                    <div className="relative z-10">
                        <h2 className="text-3xl font-black text-white tracking-tight flex items-center gap-3"><Brain className="text-medical-400" /> Theory Studio</h2>
                        <p className="text-slate-400 font-medium text-sm mt-1">Architect phased study modules for board certifications.</p>
                        
                        <div className="mt-8 flex flex-wrap gap-3">
                            <div className="bg-white/5 p-1 rounded-2xl border border-white/10 flex items-center">
                                <span className="px-4 py-2 text-[9px] font-black text-slate-500">Exam Context:</span>
                                <select 
                                    value={certId} 
                                    onChange={e => setCertId(e.target.value)} 
                                    className="bg-transparent text-white px-4 py-2 rounded-xl outline-none font-black text-[10px] cursor-pointer"
                                >
                                    {certs.map(c => <option key={c.id} value={c.id} className="text-black">{c.shortName}</option>)}
                                </select>
                            </div>
                            <button 
                                onClick={handleSuggestStrategy} 
                                disabled={isBlueprinting}
                                className="px-6 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest hover:bg-indigo-50 transition-all flex items-center gap-2 shadow-xl border-b-4 border-indigo-900 active:scale-95 disabled:opacity-50"
                            >
                                {isBlueprinting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} 
                                Suggest Modules
                            </button>
                            <button onClick={handleSave} disabled={isSaving || !selectedModule} className="px-10 py-4 bg-medical-500 text-white rounded-2xl font-black text-xs tracking-widest shadow-xl flex items-center gap-2 hover:bg-medical-400 active:scale-95 transition-all border-b-4 border-medical-700">
                                {isSaving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} Save Unit
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-3xl flex items-center justify-between animate-pop border-2 ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2 min-w-0">
                        {status.type === 'error' ? <AlertTriangle size={18} className="shrink-0" /> : <CheckCircle size={18} className="shrink-0" />}
                        <span className="font-black text-xs tracking-widest truncate">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl"><X size={16}/></button>
                </div>
            )}

            {isLoading ? (
                <div className="flex-1 flex flex-col items-center justify-center p-20 opacity-30 gap-6">
                    <Loader2 className="w-16 h-16 text-medical-600 animate-spin" />
                    <p className="font-black text-[10px] tracking-[0.4em]">Synchronizing Theory Registry...</p>
                </div>
            ) : !certId ? (
                <div className="flex-1 flex flex-col items-center justify-center p-20 bg-slate-100 rounded-[3rem] border-4 border-dashed border-slate-200">
                    <AlertTriangle size={64} className="text-slate-300 mb-6" />
                    <p className="font-black text-slate-400 tracking-widest text-center">Select an Exam Type at the top to<br/>access the Registry</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
                    <div className="xl:col-span-3 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
                        <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between font-black text-[10px] tracking-widest text-slate-400">
                            <span>Theory Sections</span>
                            <div className="relative">
                                <Search className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300" size={10} />
                                <input 
                                    type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    className="pl-4 pr-6 py-1 bg-white border border-slate-200 rounded-lg text-[8px] outline-none w-20 focus:w-28 transition-all shadow-inner"
                                    placeholder="Filter..."
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2">
                            {suggestedTopics.length > 0 && (
                                <div className="mb-6 space-y-2 animate-slide-up">
                                    <p className="px-2 text-[8px] font-black text-indigo-400 tracking-widest">AI Strategy Suggestions</p>
                                    {suggestedTopics.map(topic => (
                                        <div key={topic} className="w-full p-2 bg-indigo-50 border-2 border-dashed border-indigo-200 rounded-xl flex items-center justify-between group hover:bg-white hover:border-indigo-500 transition-all">
                                            <span className="truncate text-indigo-700 text-[10px] font-bold px-1">{topic}</span>
                                            <div className="flex gap-1 shrink-0">
                                                <button 
                                                    onClick={() => handleProvisionFromTopic(topic, true)}
                                                    className="p-1.5 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-all"
                                                    title="Provision + Build with AI"
                                                >
                                                    <Wand2 size={12} />
                                                </button>
                                                <button 
                                                    onClick={() => handleProvisionFromTopic(topic, false)}
                                                    className="p-1.5 bg-white text-indigo-600 rounded-lg border border-indigo-100 shadow-sm hover:bg-indigo-50 transition-all"
                                                    title="Provision Empty"
                                                >
                                                    <Plus size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {filteredModules.map(m => (
                                <div 
                                    key={m.id} 
                                    onClick={() => handleSelectModule(m)}
                                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between group ${selectedModule?.id === m.id ? 'bg-indigo-50 border-indigo-500 shadow-md' : 'bg-white border-transparent hover:bg-slate-50'}`}
                                >
                                    <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                                        <div className={`p-2 rounded-lg shrink-0 ${selectedModule?.id === m.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                            <BookOpen size={14} />
                                        </div>
                                        <p className="font-black text-[10px] text-slate-800 truncate">{m.title}</p>
                                        {Boolean(m.videoUrl || (m as any).hasVideo || m.slides?.some(s => s.type === 'embedded' || s.data?.embedUrl || s.data?.embedCode || s.data?.videoUrl || s.data?.youtubeUrl)) && (
                                            <span 
                                                className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-[8px] font-black uppercase tracking-wider flex items-center gap-0.5 shrink-0" 
                                                title="Video Lesson Attached"
                                            >
                                                <Video size={9} className="text-indigo-600" />
                                                Video
                                            </span>
                                        )}
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); if(confirm('Permanently purge this module?')) StorageService.deleteStudyModule(certId, m.id).then(() => setModules(modules.filter(x => x.id !== m.id))); }} className="p-1.5 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={14}/></button>
                                </div>
                            ))}
                            <div className="pt-4 grid grid-cols-1 gap-2">
                                <button onClick={() => setShowFastBuild(true)} className="w-full py-4 bg-indigo-50 border-2 border-dashed border-indigo-200 rounded-2xl text-[10px] font-black tracking-widest text-indigo-600 hover:bg-indigo-100 transition-all flex items-center justify-center gap-2">
                                    <Sparkles size={14}/> Fast-Build with AI
                                </button>
                                <button onClick={() => {
                                    const newModule: StudyModule = { 
                                        id: `module-${Date.now()}`, 
                                        title: "New Study Deck", 
                                        icon: "BookOpen", 
                                        slides: [] 
                                    };
                                    setModules([...modules, newModule]);
                                    setSelectedModule(newModule);
                                    setActiveSlideIndex(-1);
                                }} className="w-full py-4 border-2 border-dashed border-slate-100 rounded-2xl text-[10px] font-black text-slate-400 hover:text-medical-600 transition-all">+ Manual Topic</button>
                            </div>
                        </div>
                    </div>

                    <div className="xl:col-span-9 flex flex-col gap-6 min-h-0 relative">
                        {isHydrating && (
                            <div className="absolute inset-0 z-50 bg-white/60 backdrop-blur-sm flex flex-col items-center justify-center rounded-[2.5rem] animate-fade-in">
                                <div className="p-6 bg-white rounded-[2rem] shadow-2xl border border-slate-100 flex flex-col items-center gap-4">
                                    <Loader2 className="w-12 h-12 text-medical-600 animate-spin" />
                                    <p className="text-[10px] font-black text-slate-400 tracking-[0.3em]">Hydrating Theoretical Node...</p>
                                </div>
                            </div>
                        )}
                        {!selectedModule ? (
                            <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-40">
                                <Layout size={64} className="text-slate-300 mb-6" />
                                <p className="font-black text-slate-400 tracking-[0.3em] text-sm text-center">Select a Topic to<br/>Begin Construction</p>
                            </div>
                        ) : (
                            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 min-h-0">
                                <div className="lg:col-span-4 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
                                    <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                                        <span className="font-black text-[10px] tracking-widest text-slate-400">Lesson Flow</span>
                                        <button 
                                            onClick={handleBatchSynthesize}
                                            disabled={(selectedModule.slides?.length || 0) === 0}
                                            className="text-[9px] font-black text-medical-600 flex items-center gap-1.5 hover:underline disabled:opacity-30"
                                        >
                                            <Wand2 size={12}/> AI Synthesis
                                        </button>
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2">
                                        <div 
                                            onClick={() => setActiveSlideIndex(-1)}
                                            className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center gap-4 ${activeSlideIndex === -1 ? 'bg-slate-900 border-slate-900 text-white shadow-xl' : 'bg-white border-transparent hover:bg-slate-50'}`}
                                        >
                                            <div className={`p-2 rounded-lg ${activeSlideIndex === -1 ? 'bg-white/20' : 'bg-slate-100 text-slate-400'}`}><Settings size={14}/></div>
                                            <p className={`text-[10px] font-black ${activeSlideIndex === -1 ? 'text-white' : 'text-slate-800'}`}>Branding & Root</p>
                                        </div>
                                        <div className="h-px bg-slate-100 my-2 mx-2"></div>
                                        {selectedModule.slides && selectedModule.slides.map((s, i) => {
                                            const config = SLIDE_TYPES.find(t => t.type === s.type);
                                            return (
                                                <div 
                                                    key={s.id}
                                                    onClick={() => setActiveSlideIndex(i)}
                                                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between group ${activeSlideIndex === i ? 'bg-slate-900 border-slate-900 text-white shadow-xl' : 'bg-white border-transparent hover:bg-slate-50'}`}
                                                >
                                                    <div className="flex items-center gap-4 min-w-0">
                                                        <div className={`p-2 rounded-lg shadow-sm ${activeSlideIndex === i ? 'bg-white/20 text-white' : `${config?.color} text-white`}`}>
                                                            {config ? <config.icon size={14} /> : <BookOpen size={14}/>}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className={`text-[10px] font-black truncate ${activeSlideIndex === i ? 'text-white' : 'text-slate-800'}`}>{s.title || config?.label}</p>
                                                            <p className={`text-[7px] font-bold tracking-widest ${activeSlideIndex === i ? 'text-slate-400' : 'text-slate-400'}`}>{s.type}</p>
                                                        </div>
                                                    </div>
                                                    <ChevronRight size={14} className={activeSlideIndex === i ? 'opacity-100' : 'opacity-0'} />
                                                </div>
                                            );
                                        })}
                                        <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
                                            <p className="text-[9px] font-black text-slate-400 tracking-widest px-2">Manual Phase Insertion</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                {SLIDE_TYPES.map(st => (
                                                    <button key={st.type} onClick={() => addManualSlide(st.type)} className="p-3 bg-slate-50 border border-slate-100 rounded-xl text-[8px] font-black text-slate-500 hover:bg-white hover:border-medical-500 transition-all text-center">
                                                        {st.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="lg:col-span-8 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm relative">
                                    {activeSlideIndex === -1 ? (
                                        <div className="flex-1 p-12 space-y-12 overflow-y-auto custom-scrollbar">
                                            <div className="space-y-4">
                                                <h3 className="text-2xl font-black text-slate-900 tracking-tight">Educational Context</h3>
                                                <p className="text-slate-400 text-xs font-medium">Define the core subject of this curriculum partition.</p>
                                            </div>
                                            <div className="space-y-8">
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Master Topic Title</label>
                                                    <input 
                                                        type="text" 
                                                        value={selectedModule.title} 
                                                        onChange={e => updateModuleInList({...selectedModule, title: e.target.value})}
                                                        className="w-full p-6 bg-slate-50 border border-slate-100 rounded-[2rem] outline-none font-black text-2xl tracking-tighter focus:bg-white transition-all shadow-inner"
                                                        placeholder="e.g., Cardiac Monitoring Fundamentals"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Navigation Icon</label>
                                                    <input 
                                                        type="text" 
                                                        value={selectedModule.icon} 
                                                        onChange={e => updateModuleInList({...selectedModule, icon: e.target.value})}
                                                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-sm focus:bg-white"
                                                        placeholder="Lucide Icon (e.g., Activity, Heart)"
                                                    />
                                                </div>
                                                <div className="p-8 bg-indigo-50 border-2 border-indigo-100 rounded-[2rem] flex items-center justify-between group">
                                                    <div className="flex items-center gap-5">
                                                        <div className="p-4 bg-indigo-600 text-white rounded-2xl shadow-lg"><Sparkles size={24}/></div>
                                                        <div>
                                                            <p className="text-sm font-black text-indigo-900">Module Assistant</p>
                                                            <p className="text-[10px] font-bold text-indigo-600 opacity-80 leading-relaxed">"Sync all phase content automatically using board standards."</p>
                                                        </div>
                                                    </div>
                                                    <button 
                                                        onClick={handleBatchSynthesize}
                                                        disabled={(selectedModule.slides?.length || 0) === 0}
                                                        className="px-8 py-3 bg-slate-900 text-white rounded-xl font-black text-[10px] tracking-widest shadow-xl active:scale-95 disabled:opacity-30"
                                                    >
                                                        Run Global Synthesis
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                                            <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <button onClick={() => moveSlide('up')} className="p-2 hover:bg-white rounded-lg text-slate-400 hover:text-indigo-600 transition-all"><ChevronUp size={18}/></button>
                                                    <button onClick={() => moveSlide('down')} className="p-2 hover:bg-white rounded-lg text-slate-400 hover:text-indigo-600 transition-all"><ChevronDown size={18}/></button>
                                                    <div className="w-px h-6 bg-slate-200 mx-2"></div>
                                                    <h4 className="text-[10px] font-black text-slate-800 tracking-widest">{selectedModule.slides[activeSlideIndex].type} Module</h4>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <button 
                                                        onClick={handleGenerateSlideContent} 
                                                        disabled={isGeneratingSlide}
                                                        className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[9px] font-black tracking-widest flex items-center gap-2 shadow-lg active:scale-95 disabled:opacity-30"
                                                    >
                                                        {isGeneratingSlide ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12}/>} 
                                                        {isGeneratingSlide ? 'Thinking...' : 'Fill with AI'}
                                                    </button>
                                                    <button onClick={deleteSlide} className="p-2 text-slate-300 hover:text-rose-500"><Trash2 size={18}/></button>
                                                </div>
                                            </div>

                                            <div className="flex-1 overflow-y-auto p-10 custom-scrollbar space-y-8">
                                                {renderSlideForm(
                                                    selectedModule.slides[activeSlideIndex], 
                                                    updateSlideField, 
                                                    isGeneratingVisual, 
                                                    isGeneratingLogic,
                                                    isRefiningPrompt,
                                                    handleSynthesizeLogic,
                                                    handleSynthesizeVisual,
                                                    handleRefinePrompt
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const getDefaultDataForType = (type: StudySlideType) => {
    switch(type) {
        case 'overview': return { importance: "", examContext: "", observations: [] };
        case 'definitions': return { items: [{ term: "", definition: "", icon: "Info" }] };
        case 'responsibilities': return { do: [""], report: [""], dont: [""] };
        case 'process': return { steps: [""] };
        case 'examFocus': return { facts: [""], mistakes: [""] };
        case 'scenario': return { situation: "", action: "" };
        case 'review': return { summary: [""], mnemonics: [""] };
        case 'infographic': return { imageUrl: "", visualAlt: "", observations: [""] };
        case 'embedded': return { embedMode: 'url', embedUrl: "", embedCode: "", embedWidth: '100%', embedHeight: '550px' };
        default: return {};
    }
};

const renderSlideForm = (
    slide: StudySlide, 
    update: (f: string, v: any) => void, 
    isGeneratingVisual: boolean, 
    isGeneratingLogic: boolean,
    isRefiningPrompt: boolean,
    onSynthesizeLogic: () => void,
    onSynthesizeVisual: () => void,
    onRefinePrompt: () => void
) => {
    const data = slide.data;

    switch(slide.type) {
        case 'overview':
            return (
                <div className="space-y-8 animate-fade-in">
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Info size={14} className="text-medical-500" /> Pedagogical Significance</label>
                        <textarea value={data.importance} onChange={e => update('importance', e.target.value)} className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[140px] font-medium text-slate-800 outline-none focus:bg-white" placeholder="Teacher perspective on why this matters..." />
                    </div>
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Target size={14} className="text-indigo-500" /> Exam Relevance</label>
                        <textarea value={data.examContext} onChange={e => update('examContext', e.target.value)} className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[140px] font-medium text-slate-800 outline-none focus:bg-white" placeholder="How this is tested on the boards..." />
                    </div>
                    
                    <div className="space-y-6 pt-4 border-t border-slate-100">
                        <div className="flex justify-between items-center px-1">
                            <label className="text-[10px] font-black text-slate-400 tracking-widest">Observation Focus Points</label>
                            <button 
                                onClick={onSynthesizeLogic}
                                disabled={isGeneratingLogic}
                                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[9px] font-black tracking-widest transition-all shadow-xl active:scale-95 disabled:opacity-30 flex items-center gap-2"
                            >
                                {isGeneratingLogic ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12}/>}
                                Synthesize focus points
                            </button>
                        </div>
                        <ListEditor items={data.observations || []} onChange={v => update('observations', v)} placeholder="Key takeaways for student focus..." showNumbers />
                    </div>
                </div>
            );
        case 'infographic':
            return (
                <div className="space-y-10 animate-fade-in">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            <div className="p-8 bg-slate-900 rounded-[2.5rem] text-white relative overflow-hidden group border border-white/5 shadow-2xl">
                                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:rotate-12 transition-transform duration-700"><Brain size={80}/></div>
                                <div className="relative z-10 space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="text-[10px] font-black text-medical-400 tracking-widest">Synthesis Engine</h4>
                                        <button 
                                            onClick={onSynthesizeLogic}
                                            disabled={isGeneratingLogic}
                                            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[9px] font-black tracking-widest transition-all shadow-xl active:scale-95 disabled:opacity-30 flex items-center gap-2"
                                        >
                                            {isGeneratingLogic ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12}/>}
                                            {isGeneratingLogic ? 'Synthesizing...' : 'Synthesize Logic'}
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[8px] font-black text-slate-500 tracking-widest">Visual Prompt (AI Generator)</label>
                                            <button 
                                                onClick={onRefinePrompt}
                                                disabled={isRefiningPrompt}
                                                className="text-[7px] font-black text-indigo-400 hover:text-white flex items-center gap-1 transition-colors disabled:opacity-30"
                                            >
                                                {isRefiningPrompt ? <Loader2 size={8} className="animate-spin" /> : <Sparkles size={8} />}
                                                Refine with AI
                                            </button>
                                        </div>
                                        <textarea 
                                            value={data.visualAlt || ""} 
                                            onChange={e => update('visualAlt', e.target.value)}
                                            className="w-full p-4 bg-white/5 border border-white/10 rounded-2xl outline-none font-bold text-xs h-24 focus:bg-white/10 transition-all shadow-inner custom-scrollbar"
                                            placeholder="Detailed description for the AI illustrator..."
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[8px] font-black text-slate-500 tracking-widest flex items-center gap-1.5"><LinkIcon size={10}/> Manual Asset URL</label>
                                        <input 
                                            type="text" 
                                            value={data.imageUrl || ""} 
                                            onChange={e => update('imageUrl', e.target.value)}
                                            className="w-full p-3 bg-white/5 border border-white/10 rounded-xl outline-none font-mono text-[9px] focus:bg-white/10"
                                            placeholder="Paste image URL here..."
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Checkpoints</label>
                                <ListEditor items={data.observations || []} onChange={v => update('observations', v)} placeholder="Learning observation..." showNumbers />
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="p-8 bg-white border border-slate-200 shadow-sm space-y-6">
                                <div className="flex justify-between items-center">
                                    <h4 className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                                        <ImageIcon size={14} className="text-indigo-600" /> Medical Asset
                                    </h4>
                                    <button 
                                        onClick={onSynthesizeVisual}
                                        disabled={isGeneratingVisual || !data.visualAlt}
                                        className="px-6 py-2 bg-slate-900 hover:bg-black text-white rounded-xl text-[9px] font-black tracking-widest transition-all shadow-xl active:scale-95 disabled:opacity-30 flex items-center gap-2"
                                    >
                                        {isGeneratingVisual ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12}/>}
                                        {isGeneratingVisual ? 'Illustrating...' : 'Generate Illustration'}
                                    </button>
                                </div>

                                <div className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] overflow-hidden flex items-center justify-center relative group">
                                    {data.imageUrl ? (
                                        <>
                                            <img src={data.imageUrl} className="w-full h-full object-contain bg-slate-900" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <button onClick={() => update('imageUrl', '')} className="p-4 bg-rose-600 text-white rounded-full shadow-2xl transition-all active:scale-95"><Trash2 size={24}/></button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center p-8 opacity-20">
                                            <ImageIcon size={48} className="mx-auto mb-4" />
                                            <p className="font-black text-[10px] tracking-[0.2em]">Asset Sync Required</p>
                                        </div>
                                    )}
                                    {isGeneratingVisual && (
                                        <div className="absolute inset-0 bg-white/80 backdrop-blur-md flex flex-col items-center justify-center gap-4 z-20">
                                            <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                                            <p className="font-black text-[8px] tracking-[0.4em] text-indigo-900 animate-pulse">Synthesizing clinical visual...</p>
                                        </div>
                                    )}
                                </div>
                                <div className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-2xl flex items-start gap-3">
                                    <Info size={14} className="text-indigo-600 mt-0.5 shrink-0" />
                                    <p className="text-[10px] font-bold text-indigo-900 leading-relaxed opacity-80">
                                        "Generation is optimized for anatomical accuracy. Use the refinement tool to improve prompt specificity."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        case 'definitions':
            return (
                <div className="space-y-6 animate-fade-in">
                    <div className="flex justify-between items-center px-1">
                        <label className="text-[10px] font-black text-slate-400 tracking-widest">Glossary Matrix</label>
                    </div>
                    <div className="space-y-4">
                        {(data.items || []).map((item: any, idx: number) => (
                            <div key={idx} className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-6 relative group animate-pop shadow-inner">
                                <button onClick={() => update('items', data.items.filter((_: any, i: number) => i !== idx))} className="absolute top-4 right-4 p-2 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all"><X size={14}/></button>
                                <div className="space-y-2">
                                    <label className="text-[8px] font-black text-slate-400">Term</label>
                                    <input type="text" value={item.term} onChange={e => {
                                        const next = [...data.items]; next[idx].term = e.target.value; update('items', next);
                                    }} className="w-full p-4 bg-white border border-slate-200 rounded-xl font-black text-xs" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[8px] font-black text-slate-400">Definition</label>
                                    <input type="text" value={item.definition} onChange={e => {
                                        const next = [...data.items]; next[idx].definition = e.target.value; update('items', next);
                                    }} className="w-full p-4 bg-white border border-slate-200 rounded-xl font-bold text-xs" />
                                </div>
                            </div>
                        ))}
                        <button onClick={() => update('items', [...data.items, { term: "", definition: "", icon: "Info" }])} className="w-full py-4 border-2 border-dashed border-slate-100 rounded-[1.5rem] text-[10px] font-black text-slate-400 hover:text-emerald-600 transition-all">+ Add Term</button>
                    </div>
                </div>
            );
        case 'responsibilities':
            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-fade-in">
                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-emerald-600 tracking-widest ml-1 flex items-center gap-2"><CheckCircle size={14}/> Authorized (Do)</label>
                        <ListEditor items={data.do} onChange={v => update('do', v)} placeholder="Tasks within scope..." />
                    </div>
                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-indigo-600 tracking-widest ml-1 flex items-center gap-2"><MessageSquare size={14}/> Trigger (Report)</label>
                        <ListEditor items={data.report} onChange={v => update('report', v)} placeholder="Events to report to RN..." />
                    </div>
                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-rose-600 tracking-widest ml-1 flex items-center gap-2"><Ban size={14}/> Restricted (Dont)</label>
                        <ListEditor items={data.dont} onChange={v => update('dont', v)} placeholder="Tasks outside scope..." />
                    </div>
                </div>
            );
        case 'process':
            return (
                <div className="space-y-6 animate-fade-in">
                    <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><ClipboardList size={16} className="text-amber-500" /> Clinical Progression</label>
                    <ListEditor items={data.steps} onChange={v => update('steps', v)} placeholder="Clinical step..." showNumbers />
                </div>
            );
        case 'examFocus':
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 animate-fade-in">
                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-medical-600 tracking-widest ml-1 flex items-center gap-2"><Lightbulb size={16}/> Board Truths</label>
                        <ListEditor items={data.facts} onChange={v => update('facts', v)} placeholder="High-yield fact..." />
                    </div>
                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-orange-600 tracking-widest ml-1 flex items-center gap-2"><ShieldAlert size={16}/> Common Traps</label>
                        <ListEditor items={data.mistakes} onChange={v => update('mistakes', v)} placeholder="Standard exam mistake..." />
                    </div>
                </div>
            );
        case 'scenario':
            return (
                <div className="space-y-8 animate-fade-in">
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Clinical Scenario Context</label>
                        <textarea value={data.situation} onChange={e => update('situation', e.target.value)} className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[140px] font-black text-lg text-slate-700 outline-none focus:bg-white shadow-inner" placeholder="The setup..." />
                    </div>
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-emerald-600 tracking-widest ml-1">Best Practice Action</label>
                        <textarea value={data.action} onChange={e => update('action', e.target.value)} className="w-full p-6 bg-emerald-50/30 border border-emerald-100 rounded-3xl min-h-[140px] font-black text-lg text-emerald-900 outline-none focus:bg-white" placeholder="The correct nursing decision..." />
                    </div>
                </div>
            );
        case 'review':
            return (
                <div className="space-y-8 animate-fade-in">
                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-indigo-600 tracking-widest ml-1 flex items-center gap-2"><Star size={16}/> Synthesis Summary</label>
                        <ListEditor items={data.summary} onChange={v => update('summary', v)} placeholder="Key takeaway..." />
                    </div>
                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-purple-600 tracking-widest ml-1 flex items-center gap-2"><Brain size={16}/> Memory Aids</label>
                        <ListEditor items={data.mnemonics} onChange={v => update('mnemonics', v)} placeholder="Acronym or logic aid..." />
                    </div>
                </div>
            );
        case 'embedded':
            return (
                <div className="space-y-8 animate-fade-in">
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                            <FileText size={14} className="text-indigo-500" /> Embedded Phase Title
                        </label>
                        <input 
                            type="text" 
                            value={slide.title || ''} 
                            onChange={e => update('title', e.target.value)} 
                            className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-black text-xs outline-none focus:bg-white" 
                            placeholder="e.g., Google Slides Chapter 1, Manual Content Phase" 
                        />
                    </div>

                    <div className="space-y-4">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                            <Settings size={14} className="text-indigo-500" /> Embedding Option / Mode
                        </label>
                        <div className="flex gap-4">
                            <button 
                                type="button"
                                onClick={() => update('embedMode', 'url')}
                                className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-2 ${
                                    data.embedMode === 'url' 
                                        ? 'bg-indigo-600 text-white shadow-md' 
                                        : 'bg-slate-50 border border-slate-100 text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                <LinkIcon size={12} /> Option 1: External URL Iframe
                            </button>
                            <button 
                                type="button"
                                onClick={() => update('embedMode', 'code')}
                                className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-2 ${
                                    data.embedMode === 'code' 
                                        ? 'bg-indigo-600 text-white shadow-md' 
                                        : 'bg-slate-50 border border-slate-100 text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                <FileText size={12} /> Option 3: Raw Embedded HTML Code
                            </button>
                        </div>
                    </div>

                    {data.embedMode === 'url' ? (
                        <div className="space-y-3 animate-fade-in">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <LinkIcon size={14} className="text-indigo-500" /> External Resource URL
                            </label>
                            <input 
                                type="text" 
                                value={data.embedUrl || ''} 
                                onChange={e => {
                                    update('embedUrl', e.target.value);
                                }} 
                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-mono text-xs outline-none focus:bg-white" 
                                placeholder="Paste clean website, slides presentation, or YouTube link here (e.g. https://www.youtube.com/watch?v=...)" 
                            />
                            {(() => {
                                const currentUrl = data.embedUrl || '';
                                const details = inspectEmbedUrl(currentUrl);
                                if (details.isBlockedRoot) {
                                    return (
                                        <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs font-semibold">
                                            <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-bold">Specific Video Link Required</p>
                                                <p className="text-[11px] text-amber-700 mt-0.5">
                                                    The YouTube home page (<code>https://www.youtube.com/</code>) sets <code>X-Frame-Options: SAMEORIGIN</code> and cannot be embedded in an iframe.
                                                    Please paste a specific video URL (e.g. <code>https://www.youtube.com/watch?v=...</code> or <code>https://youtu.be/...</code>).
                                                </p>
                                            </div>
                                        </div>
                                    );
                                }
                                if (details.provider === 'youtube' && details.embedUrl) {
                                    return (
                                        <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-[10px] font-bold">
                                            <CheckCircle size={14} className="text-emerald-500 shrink-0" />
                                            <span>YouTube video configured for embed playback without X-Frame-Options blocking.</span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                                <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Supports YouTube links, Vimeo, Google Slides, or clean https:// URLs.</p>
                                <button
                                    type="button"
                                    onClick={() => update('embedUrl', 'https://www.youtube.com/watch?v=3PmVJQUCm4E')}
                                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 underline decoration-dotted cursor-pointer"
                                >
                                    Insert Sample Video
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3 animate-fade-in">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <FileText size={14} className="text-indigo-500" /> Raw HTML / Embed Code Snippet
                            </label>
                            <textarea 
                                value={data.embedCode || ''} 
                                onChange={e => update('embedCode', e.target.value)} 
                                className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[160px] font-mono text-xs outline-none focus:bg-white resize-none shadow-inner" 
                                placeholder="Paste your HTML iframe embed code (e.g. <iframe src='...'></iframe>) or any custom HTML widgets..." 
                            />
                            <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mt-1">Make sure script tags match sandbox policies for safe execution.</p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <Maximize2 size={14} className="text-indigo-550" /> Frame Width
                            </label>
                            <input 
                                type="text" 
                                value={data.embedWidth || '100%'} 
                                onChange={e => update('embedWidth', e.target.value)} 
                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-mono text-xs outline-none focus:bg-white" 
                                placeholder="Default: 100%" 
                            />
                            <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mt-1">e.g., 100% or 800px</p>
                        </div>
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <Maximize2 size={14} className="text-indigo-555" /> Frame Height
                            </label>
                            <input 
                                type="text" 
                                value={data.embedHeight || '300px'} 
                                onChange={e => update('embedHeight', e.target.value)} 
                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-mono text-xs outline-none focus:bg-white" 
                                placeholder="Default: 300px" 
                            />
                            <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mt-1">e.g., 300px or 500px</p>
                        </div>
                    </div>

                    {/* Live Preview Inside Editor */}
                    <div className="space-y-4 pt-6 border-t border-slate-100">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Live Embed Frame Preview</label>
                        <div className="w-full rounded-[2rem] overflow-hidden border border-slate-200 bg-slate-50 p-4 min-h-[300px] flex items-center justify-center relative">
                            {data.embedMode === 'url' || data.embedMode === 'code' ? (
                                <EmbeddedPhasePlayer 
                                    embedMode={data.embedMode}
                                    embedUrl={data.embedUrl}
                                    embedCode={data.embedCode}
                                    aspectRatio="16:9"
                                    embedWidth={data.embedWidth}
                                    embedHeight={data.embedHeight}
                                    title={selectedModule?.title}
                                    videoTitle={data.videoTitle}
                                    videoAuthor={data.videoAuthor}
                                    videoNotes={data.videoNotes}
                                />
                            ) : (
                                <div className="text-center text-slate-400 space-y-2 py-10">
                                    <LinkIcon size={32} className="mx-auto opacity-30 animate-pulse" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">Awaiting valid embed resource...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        default: return null;
    }
};

const ListEditor = ({ items, onChange, placeholder, showNumbers = false }: { items: string[], onChange: (v: string[]) => void, placeholder: string, showNumbers?: boolean }) => (
    <div className="space-y-2">
        {(items || []).map((item, idx) => (
            <div key={idx} className="flex gap-2 group animate-pop">
                {showNumbers && <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center font-black text-[10px] text-slate-500 shrink-0">{idx + 1}</div>}
                <input 
                    type="text" value={item} 
                    onChange={e => { const n = [...items]; n[idx] = e.target.value; onChange(n); }}
                    placeholder={placeholder}
                    className="flex-1 px-5 py-3 bg-white border border-slate-200 rounded-xl font-bold text-xs outline-none focus:border-indigo-500 transition-all shadow-sm"
                />
                <button onClick={() => onChange(items.filter((_, i) => i !== idx))} className="p-3 text-slate-300 hover:text-rose-500 transition-all"><Trash2 size={16}/></button>
            </div>
        ))}
        <button onClick={() => onChange([...(items || []), ""])} className="w-full py-3 border-2 border-dashed border-slate-100 rounded-xl text-[9px] font-black text-slate-400 hover:text-indigo-600 transition-all">+ Add Entry</button>
    </div>
);